import { PAYROLL_CONFIG } from './payroll.config';
import type { ParsedPayrollRow, PayrollMatchStatus } from './parsePayrollCsv';

export interface StudentMatchCandidate {
  id: string;
  externalStudentId: string | null;
  firstName: string;
  lastName: string;
  email?: string | null;
  grade?: number | null;
  deletedAt?: string | null;
  userDeletedAt?: string | null;
}

export interface MatchedPayrollRow extends ParsedPayrollRow {
  matchStatus: PayrollMatchStatus;
  studentId: string | null;
  matchedFirstName?: string;
  matchedLastName?: string;
  errorMessage: string | null;
  warnings: string[];
  isPayable: boolean;
}

export interface AlreadyPaidLookup {
  /** Set of `${studentId}::${payPeriodKey}` that already have a posted paycheck */
  keys: Set<string>;
}

function normalizeName(name: string): string {
  return (name || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function normalizeExternalId(id: string): string {
  return (id || '').trim().toLowerCase();
}

/**
 * Build a lookup map of normalized external_student_id → student.
 * If duplicates exist in DB, first active student wins and later ones are ignored.
 */
export function buildStudentIndex(students: StudentMatchCandidate[]): Map<string, StudentMatchCandidate> {
  const map = new Map<string, StudentMatchCandidate>();
  students.forEach((s) => {
    if (!s.externalStudentId) return;
    const key = normalizeExternalId(s.externalStudentId);
    if (!key) return;
    if (!map.has(key)) {
      map.set(key, s);
    }
  });
  return map;
}

/**
 * Match parsed CSV rows to students and assign match_status + payable flag.
 * Pure function — no DB access.
 */
export function matchPayrollRows(
  rows: ParsedPayrollRow[],
  students: StudentMatchCandidate[],
  payPeriodKey: string,
  alreadyPaid: AlreadyPaidLookup,
  options: { skipZeroGross?: boolean } = {}
): MatchedPayrollRow[] {
  const skipZeroGross = options.skipZeroGross ?? PAYROLL_CONFIG.skipZeroGross;
  const index = buildStudentIndex(students);

  // Detect duplicate external IDs within the file
  const idCounts = new Map<string, number>();
  rows.forEach((row) => {
    const key = normalizeExternalId(row.externalStudentId);
    if (!key) return;
    idCounts.set(key, (idCounts.get(key) || 0) + 1);
  });

  return rows.map((row) => {
    const warnings: string[] = [...(row.warnings || [])];
    const externalKey = normalizeExternalId(row.externalStudentId);
    let matchStatus: PayrollMatchStatus = 'unmatched';
    let studentId: string | null = null;
    let matchedFirstName: string | undefined;
    let matchedLastName: string | undefined;
    let errorMessage: string | null = null;
    let isPayable = false;

    // 1) Parse / validation errors (invalid periods, missing fields)
    if (row.parseErrors.length > 0 || row.invalidPeriodKeys.length > 0) {
      matchStatus = 'invalid_grade';
      errorMessage = row.parseErrors.join('; ') || 'Invalid period grades';
      return {
        ...row,
        matchStatus,
        studentId,
        errorMessage,
        warnings,
        isPayable: false,
      };
    }

    // 2) Duplicate student ID in same file
    if (externalKey && (idCounts.get(externalKey) || 0) > 1) {
      matchStatus = 'duplicate_in_file';
      errorMessage = 'Duplicate Student ID in this file — fix and re-upload';
      return {
        ...row,
        matchStatus,
        studentId,
        errorMessage,
        warnings,
        isPayable: false,
      };
    }

    // 3) Zero gross
    if (skipZeroGross && row.gross <= 0) {
      matchStatus = 'zero_gross';
      errorMessage = 'All periods blank or zero — skipped';
      return {
        ...row,
        matchStatus,
        studentId,
        errorMessage,
        warnings,
        isPayable: false,
      };
    }

    // 4) Match by external ID
    const student = externalKey ? index.get(externalKey) : undefined;
    if (!student) {
      matchStatus = 'unmatched';
      errorMessage = externalKey
        ? `No student found with ID "${row.externalStudentId}"`
        : 'Missing Student ID';
      return {
        ...row,
        matchStatus,
        studentId,
        errorMessage,
        warnings,
        isPayable: false,
      };
    }

    // Soft-deleted / inactive
    if (student.deletedAt || student.userDeletedAt) {
      matchStatus = 'inactive';
      errorMessage = 'Student account is inactive or deleted';
      studentId = student.id;
      return {
        ...row,
        matchStatus,
        studentId,
        matchedFirstName: student.firstName,
        matchedLastName: student.lastName,
        errorMessage,
        warnings,
        isPayable: false,
      };
    }

    studentId = student.id;
    matchedFirstName = student.firstName;
    matchedLastName = student.lastName;

    // 5) Already paid this period
    const paidKey = `${studentId}::${payPeriodKey}`;
    if (alreadyPaid.keys.has(paidKey)) {
      matchStatus = 'already_paid';
      errorMessage = `Already paid for period ${payPeriodKey}`;
      return {
        ...row,
        matchStatus,
        studentId,
        matchedFirstName,
        matchedLastName,
        errorMessage,
        warnings,
        isPayable: false,
      };
    }

    // 6) Name mismatch warning (still payable)
    const csvFirst = normalizeName(row.firstName);
    const csvLast = normalizeName(row.lastName);
    const dbFirst = normalizeName(student.firstName);
    const dbLast = normalizeName(student.lastName);
    const nameMismatch = csvFirst !== dbFirst || csvLast !== dbLast;

    if (nameMismatch) {
      matchStatus = 'name_mismatch_warning';
      warnings.push(
        `Name in uploaded file "${row.firstName} ${row.lastName}" differs from the student account on file "${student.firstName} ${student.lastName}"`
      );
      isPayable = true;
    } else {
      matchStatus = 'matched';
      isPayable = true;
    }

    return {
      ...row,
      matchStatus,
      studentId,
      matchedFirstName,
      matchedLastName,
      errorMessage: null,
      warnings,
      isPayable,
    };
  });
}

export function summarizeMatchedRows(rows: MatchedPayrollRow[]) {
  const payable = rows.filter((r) => r.isPayable);
  const problems = rows.filter((r) => !r.isPayable);
  const warnings = rows.filter((r) => r.matchStatus === 'name_mismatch_warning');

  const totalGross = payable.reduce((s, r) => s + r.gross, 0);
  const totalTax = payable.reduce((s, r) => s + r.taxWithheld, 0);
  const totalNet = payable.reduce((s, r) => s + r.net, 0);

  return {
    matchedCount: payable.length,
    errorCount: problems.length,
    warningCount: warnings.length,
    totalRows: rows.length,
    totalGross: Math.round(totalGross * 100) / 100,
    totalTax: Math.round(totalTax * 100) / 100,
    totalNet: Math.round(totalNet * 100) / 100,
  };
}
